import { Box, Link, Typography, alpha } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import { CatalogItemsSelectedCounter } from 'entities/CatalogItem';
import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getCatalogItemsShowDetailedView,
    getCatalogItemsTotalCount,
    getSelectedCatalogItem,
    getSelectedCatalogItemsTotal,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { AddToPropertyCatalogButton } from 'features/AddCatalogItem';
import { CreateOrderWithItemsButton } from 'features/AddItemsToOrder';
import {
    SearchCatalogItemsField,
    SearchGlobalCatalogItems,
} from 'features/SearchCatalogItems';
import { useMarketplaceNavigation } from 'features/SearchCatalogItems/lib/hooks/useMarketplaceNavigation';
import {
    getSearchCatalogItemIsActive,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { itemsContainerHeightOffset } from 'shared/const/styles';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import {
    PropertyCatalogItemDataDefiner,
    PropertyCatalogItemsList,
    PropertyCatalogItemsMoveTo,
} from 'widgets/PropertyCatalog';

interface SearchCatalogItemsProps {
    catalogId: string;
    expandable?: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    isInventory?: boolean;
    showEditButtons?: boolean;
}

export const SearchCatalogItems = ({
    catalogId,
    expandable,
    moveTo,
    isInventory = false,
    showEditButtons = false,
}: SearchCatalogItemsProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const isActive = useSelector(getSearchCatalogItemIsActive);
    const isLoading = useSelector(getCatalogItemsAreLoading);
    const catalogItems = useSelector(getCatalogItems);
    const resultsCount = useSelector(getCatalogItemsTotalCount);
    const totalItemsCount = useSelector(getSelectedCatalogItemsTotal);
    const selectedItem = useSelector(getSelectedCatalogItem);
    const detailedViewIsShown = useSelector(getCatalogItemsShowDetailedView);
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);

    const { navigateToMarketplace } = useMarketplaceNavigation();

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderEditing = location.state?.selectedOrderId;
    const isMoveToCatalog = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const isMoveToOrder = moveTo === PropertyCatalogItemsMoveTo.ORDER;
    const isExpanded = expandable && isActive;

    const resultWord = resultsCount === 1 ? t('Result') : t('Results');

    const isInOrderSection = location.pathname.includes('/orders/');

    const actionBarHeight = isMoveToCatalog ? 90 : 144;

    const itemsContainerHeightWithActions = `calc(100vh - ${
        itemsContainerHeightOffset + actionBarHeight
    }px)`;

    const showExpandedView = selectedItem && detailedViewIsShown;

    const showNoResultsMessage =
        isExpanded &&
        !isLoading &&
        resultsCount === 0 &&
        localSearchValue.length > 0 &&
        !isGlobalCatalog &&
        isMoveToCatalog;

    useEffect(
        () => () => {
            dispatch(searchCatalogItemsActions.resetSearch());
        },
        [dispatch],
    );

    return (
        <Box>
            <Box
                flexBasis="50%"
                display={showExpandedView && isExpanded ? 'none' : 'flex'}
                flexDirection="column"
                borderRadius="8px"
                border="1px solid"
                borderColor="divider"
            >
                {isGlobalCatalog ? (
                    <SearchGlobalCatalogItems hideBorder />
                ) : (
                    <SearchCatalogItemsField
                        catalogId={catalogId}
                        hideBorder
                        isInventory={isInventory}
                    />
                )}
                {isExpanded && (
                    <Box
                        padding="0 16px 16px"
                        maxHeight={itemsContainerHeightWithActions}
                        sx={{ overflowX: 'hidden', overflowY: 'scroll' }}
                    >
                        {!isLoading && !showNoResultsMessage && (
                            <Box
                                position="sticky"
                                top="0"
                                p="11px 0"
                                borderBottom="1px solid"
                                borderColor="divider"
                                bgcolor="background.paper"
                                zIndex="drawer"
                            >
                                <Typography
                                    typography="openSans.body2Medium"
                                    color={(theme) =>
                                        alpha(theme.palette.common.black, 0.5)
                                    }
                                >
                                    {resultsCount} {resultWord} {t('Found')}
                                </Typography>
                            </Box>
                        )}
                        {showNoResultsMessage && (
                            <Box
                                bgcolor="background.paper"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Typography
                                    typography="openSans.body2Medium"
                                    sx={(theme) => ({
                                        lineHeight: '24px',
                                        fontWeight: 400,
                                        color: alpha(
                                            theme.palette.common.black,
                                            0.5,
                                        ),
                                    })}
                                >
                                    {t(
                                        'No items found in the property catalog',
                                    )}
                                    .
                                </Typography>
                                {showEditButtons && (
                                    <Link
                                        data-testid="search-marketplace-link"
                                        component="button"
                                        typography="openSans.body2Medium"
                                        sx={{
                                            lineHeight: '24px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                        underline="hover"
                                        onClick={navigateToMarketplace}
                                    >
                                        {t('Search the RE-OPS Marketplace')}.
                                    </Link>
                                )}
                            </Box>
                        )}
                        <PropertyCatalogItemsList
                            items={catalogItems}
                            moveTo={moveTo}
                            loading={isLoading}
                            quickEdit={isOrderEditing}
                            isSearch
                        />
                    </Box>
                )}
                {isExpanded && !showNoResultsMessage && (
                    <Box display="flex" justifyContent="flex-end" mr="16px">
                        <Box>
                            {!isInOrderSection && (
                                <CatalogItemsSelectedCounter />
                            )}
                            <Box
                                mb="16px"
                                width="250px"
                                display="flex"
                                justifyContent="flex-end"
                            >
                                {isMoveToCatalog && isGlobalCatalog && (
                                    <AddToPropertyCatalogButton
                                        disabled={totalItemsCount === 0}
                                    />
                                )}
                                {isMoveToOrder && (
                                    <CreateOrderWithItemsButton />
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
            {isExpanded && showExpandedView && (
                <Box
                    p="16px"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="divider"
                >
                    <PropertyCatalogItemDataDefiner
                        isDetailed
                        item={selectedItem}
                        moveTo={moveTo}
                        quickEdit={isOrderEditing}
                        isSearch
                    />
                </Box>
            )}
        </Box>
    );
};
